import { getFormattedPhone } from "utils/getFormattedDate";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { IoLocationOutline } from "react-icons/io5";
import { MdOutlineMail } from "react-icons/md";
import { FiPhone } from "react-icons/fi";

export const formatAsyncSelect = (facility) => {
  if (!facility) return null;
  const { organizationsAddress, ...restFacility } = facility;
  const line_1 = organizationsAddress?.line_1 || "";
  const line_2 = organizationsAddress?.line_2 || "";
  const city = organizationsAddress?.city || "";
  const state = organizationsAddress?.state || "";
  const zip = organizationsAddress?.zip || "";
  return {
    value: restFacility.id,
    label: restFacility?.name,
    fullLabel: (
      <>
        <p>{restFacility?.name} </p>
        <p>
          {line_1}
          {line_2 && ` ${line_2}`}
        </p>
        <p>
          {city}
          {state && `, ${state}`} {zip}
        </p>
      </>
    ),
  };
};

// selectionHelper.js
export function extractSelectionFromRows(rows, key = "isSelected") {
  const rowSelection = {};
  const selectedRows = [];

  rows.forEach((row, index) => {
    if (row[key]) {
      rowSelection[index] = true;
      selectedRows.push(row);
    }
  });

  return { rowSelection, selectedRows };
}

//apiHelpers.js
export const buildQueryParams = (payload, defaultParams = {}) => {
  const finalParams = {
    ...defaultParams,
    ...payload,
  };

  const params = new URLSearchParams();
  Object.entries(finalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== null) {
      params.append(key, value);
    }
  });

  return params.toString();
};

export const formatBackend = (fax) => {
  if (!fax) return "";
  return fax.replace(/\D/g, "");
};

const addressText = (record, ...keys) => {
  if (!record || typeof record !== "object") return "";
  for (const key of keys) {
    const value = record[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (!text || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
      continue;
    }
    return text;
  }
  return "";
};

export const profileAddressFields = (data) => {
  const sources = [
    data?.useraddress,
    data?.userAddress,
    data?.adminaddress,
    data?.account,
    data?.account?.address,
    data?.account?.organizationsAddress,
  ];
  const pick = (...keys) => {
    for (const source of sources) {
      const text = addressText(source, ...keys);
      if (text) return text;
    }
    return "";
  };

  return {
    line1: pick("line_1", "line1", "addressLine1"),
    line2: pick("line_2", "line2", "addressLine2"),
    city: pick("city"),
    state: pick("state"),
    zipcode: pick("zip", "zipcode", "zipCode"),
  };
};

export const formatFacilityDetails = (facility, type = null) => {
  const address = facility?.address ?? facility?.organizationsAddress ?? facility;
  const street1 = addressText(address, "line_1", "line1");
  const street2 = addressText(address, "line_2", "line2");

  if (!facility && type !== "Requester Information") {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-full">
        <HiOutlineBuildingOffice2 size={20} />
        <i className="font-semibold text-gray-400 dark:text-gray-500">
          No facility selected
        </i>
        <p className="text-center">Search and select an organization above</p>
      </div>
    );
  } else if(!facility) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-full">
        <HiOutlineBuildingOffice2 size={20} />
        <i className="font-semibold text-gray-400 dark:text-gray-500">
          No requester selected
        </i>
        <p>select a requester above</p>
      </div>
    );
  }

  return (
    <div className="h-full">
      {type && (
        <p className="font-semibold text-md px-4 py-2 bg-gray-200 dark:bg-[#373737] text-black dark:text-white rounded-t-md">
          {type}
        </p>
      )}

      <div className="border-b border-gray-300 dark:border-gray-600 w-full"></div>

      <div className="text-black dark:text-white text-left space-y-1 px-4 py-2">
        <p className="font-semibold text-[14px] break-words whitespace-normal">
          {facility?.name}
        </p>

        {address &&
          <div>
            {(street1 || street2) && (
              <div className="flex gap-1 items-center">
                <IoLocationOutline />
                <p className="break-all whitespace-normal">
                  {[street1, street2].filter(Boolean).join(" ")}
                </p>
              </div>
            )}

            <p className="break-all whitespace-normal ml-[15px]">
              {address?.city}
              {address?.state ? `, ${address?.state} ` : ""} {address?.zip}
            </p>
          </div>
        }

        {facility?.supportEmail &&
          <div className="flex items-center gap-1">
            <MdOutlineMail />
            <p className="break-words whitespace-normal">
              {facility?.supportEmail}
            </p>
          </div>
        }

        {facility?.supportPhoneNumber &&
          <div className="flex items-center gap-1">
            <FiPhone />
            <p className="break-words whitespace-normal">
              {getFormattedPhone(facility?.supportPhoneNumber)}
            </p>
          </div>
        }
      </div>
    </div>
  );
};

export const formatFacilityDetailsString = (facility) => {
  if (!facility) return "N/A";

  const address = facility?.address ?? facility?.organizationsAddress ?? facility;

  // prefer support values, fallback to regular ones
  const email = facility?.support_email;
  const phone = getFormattedPhone(facility?.phoneNumber);
  const lines = [
    facility?.name,
    [addressText(address, "line_1", "line1"), addressText(address, "line_2", "line2")]
      .filter(Boolean)
      .join(" "),
    [address?.city, [address?.state, address?.zip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
    email,
    phone,
  ];

  return lines.filter(Boolean).join("\n");
};

const invoiceAddressPart = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
    return "";
  }
  return text;
};

const looksLikePhoneNumber = (value) => /^\d{7,}$/.test(String(value).replace(/[\s().-]/g, ""));

export const formatInvoiceAddress = (...sources) => {
  const records = sources.filter((source) => source && typeof source === "object");
  const pick = (...keys) => {
    for (const record of records) {
      const pools = [
        record,
        record.dataValues,
        record.address,
        record.address?.dataValues,
        record.organizationsAddress,
        record.useraddress,
      ].filter(Boolean);
      for (const pool of pools) {
        for (const key of keys) {
          const text = invoiceAddressPart(pool[key]);
          if (text) return text;
        }
      }
    }
    return "";
  };

  const street = [
    pick("line1", "line_1", "addressLine1", "requesterAddressLine1"),
    pick("line2", "line_2", "addressLine2", "requesterAddressLine2"),
  ]
    .filter(Boolean)
    .join(" ");
  const cityCandidates = [];
  for (const record of records) {
    const pools = [
      record,
      record.dataValues,
      record.address,
      record.address?.dataValues,
      record.organizationsAddress,
      record.useraddress,
    ].filter(Boolean);
    for (const pool of pools) {
      for (const key of ["city", "requesterCity", "requesterAddressCity"]) {
        const text = invoiceAddressPart(pool[key]);
        if (text && !cityCandidates.includes(text)) cityCandidates.push(text);
      }
    }
  }
  const city =
    cityCandidates.find((text) => !looksLikePhoneNumber(text)) || "";
  const locality = [
    city,
    pick("state", "requesterState", "requesterAddressState"),
  ]
    .filter(Boolean)
    .join(", ");
  const zip = pick(
    "zip",
    "zipcode",
    "zipCode",
    "requesterZip",
    "requesterZipCode",
    "requesterAddressZip",
  );

  return [street, [locality, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join("\n");
};

export const centsToDollars = (amount) => {
  return (Number(amount) / 100).toFixed(2);
};

export const dollarsToCents = (amount) => {
  return Math.round(Number(amount) * 100);
};

const PROCESSING_FEE_TYPE_VALUES = new Set([1, 2, "1", "2"]);
const PROCESSING_FEE_TYPE_NAMES = new Set([
  "online_fee",
  "check_fee",
  "online fee",
  "check fee",
  "online",
  "check",
  "electronic processing fee",
  "check processing fee",
]);

export const isInvoiceProcessingFee = (fee) => {
  if (!fee) return false;
  if (PROCESSING_FEE_TYPE_VALUES.has(fee.type)) return true;

  const name = String(fee.feeType || fee.typeName || fee.name || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  return (
    PROCESSING_FEE_TYPE_NAMES.has(name) ||
    name.includes("online fee") ||
    name.includes("check fee")
  );
};

export const getNonProcessingInvoiceFees = (fees = []) =>
  (fees || []).filter((fee) => !isInvoiceProcessingFee(fee));

export const getProcessingInvoiceFeeAmount = (fees = []) =>
  (fees || [])
    .filter(isInvoiceProcessingFee)
    .reduce((sum, fee) => sum + Number(fee.feeAmount || fee.amount || 0), 0);

export const formatRecordDateRange = (range) => {
  if (!range) return "N/A";

  // Case: 0 means "All"
  if (range.rangeType === 1) {
    return "All";
  }

  // Case: 1 means custom date range
  if (range.rangeType === 2) {
    const { recordsFrom, recordsTo } = range;

    if (!recordsFrom && !recordsTo) return "Custom (no dates)";
    if (recordsFrom && recordsTo) return `${recordsFrom} → ${recordsTo}`;
    if (recordsFrom) return `From: ${recordsFrom}`;
    if (recordsTo) return `To: ${recordsTo}`;
  }

  return "N/A";
};

export const formatDateRange = (range, priority) => {
  if (!range) return "N/A";

  const { rangeType, recordsFrom, recordsTo } = range;

  // Case: 1 means "All"
  if (rangeType === 1) {
    return "ALL";
  }

  if (rangeType === 2) {
    const formatDate = (dateStr) => {
      if (!dateStr) return null;

      const date = new Date(dateStr);
      if (isNaN(date)) return null;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    };

    const from = formatDate(recordsFrom);
    const to = formatDate(recordsTo);

    // Priority === 2: always show both
    if (priority === 2) {
      return `${from || "N/A"} → ${to || "N/A"}`;
    }

    if (!from && !to) return "Custom (no dates)";
    if (from && to) return `${from} → ${to}`;
    if (from) return `From: ${from}`;
    if (to) return `To: ${to}`;
  }

  return "N/A";
};

export const REQUEST_TYPE = {
  DISABILITY_REQUEST: "Disability Request",
};

export const IS_AUDIT_TYPE = {
  MULTIPLE_TYPE: "MULTIPLE",
  SINGLE_TYPE: "SINGLE",
};


export const AZURE_BLOB_BASE_URL =
  "https://ezroifuncprodbgjobsfiles.blob.core.windows.net/public/";


export const REQUEST_INTERNAL_STATUS = {
  NEW_REQUEST: { name: 'Request to Review', value: 1 },
  DOCUMENT_UNDER_REVIEW: { name: 'Document Under Review', value: 2 },
  REJECTED: { name: 'Rejected', value: 3 },
  APPROVED: { name: 'Retrieve Records', value: 4 },
  SEARCHING_FOR_RECORDS: { name: 'Searching For Records', value: 5 },
  NO_RECORDS_FOUND: { name: 'No Records Found', value: 6 },
  READY_TO_INVOICE_RI: { name: 'Send Invoices', value: 7 },
  PENDING_PAYMENT: { name: 'Pending Payment', value: 8 },
  BD_PARTS_ADDED: { name: 'BD Parts Added', value: 9 },
  X_RAY_BD_PENDING: { name: 'Imaging BD Pending', value: 10 },
  X_RAY_BD_DUPLICATION: { name: 'Imaging BD Duplication', value: 11 },
  X_RAY_BD_FILES_IMPORTED: { name: 'Imaging BD RTI', value: 12 },
  READY_FOR_DOWNLOAD: { name: 'Download Files', value: 13 },
  COMPLETED: { name: 'Completed', value: 14 },
  X_RAY_WAITING_FOR_APPROVAL: { name: 'Imaging Waiting For Approval', value: 15 },
  X_RAY_APPROVED: { name: 'Retrieve Imaging', value: 16 },
  ADVANCE_FEE_PENDING: { name: 'Pending Advance', value: 17 },
  RESUBMITTED: { name: 'Resubmitted', value: 18 },
  CANCELLED: { name: 'Cancelled', value: 19 },
  DRAFT: { name: 'Draft', value: 20 },
  DOCUMENTS_FOUND_AND_ATTACHED: { name: 'Documents Found and Attached', value: 21 },
  DOCUMENTS_NOT_FOUND: { name: 'Documents Not Found', value: 22 },
}

export const REQUEST_INTERNAL_STATUS_VALUE_TO_NAME_MAP = Object.values(
  REQUEST_INTERNAL_STATUS
).reduce((acc, status) => {
  acc[status.value] = status.name;
  return acc;
}, {});


export const REQUEST_STATUS_TYPES = {
  ORDER_RECEIVED: { label: 'Order Received', value: 1 },
  ORDER_IN_PROCESS: { label: 'Order In Process', value: 2 },
  REJECTED: { label: 'Rejected', value: 3 },
  READY_FOR_PAYMENT: { label: 'Ready For Payment', value: 4 },
  READY_FOR_DOWNLOAD: { label: 'Download Files', value: 5 },
  COMPLETED: { label: 'Completed', value: 6 },
  X_RAY_DOWNLOAD: { label: 'Imaging Download', value: 7 },
  X_RAY_WAITING_FOR_APPROVAL: { label: 'Imaging Waiting For Approval', value: 8 },
  X_RAY_APPROVED: { label: 'Retrieve Imaging', value: 9 },
  ADVANCE_FEE_PENDING: { label: 'Pending Advance', value: 10 },
  RESUBMITTED: { label: 'Resubmitted', value: 11 },
  CANCELLED: { label: 'Cancelled', value: 12 },
  DRAFT: { label: 'Draft', value: 13 }
}

export const REQUEST_STATUS_VALUE_TO_NAME_MAP = Object.values(
  REQUEST_STATUS_TYPES
).reduce((acc, status) => {
  acc[status.value] = status.label;
  return acc;
}, {});

export const resolveOrgTypeFromUser = (userData) => {
  const role = userData?.authority?.[0];
  if (role === "FacilityAdmin") return "MEDICAL_FACILITY";
  if (role === "AttorneyOffice") return "LAW_OFFICE";

  const nature = userData?.account?.nature;
  if (
    nature === "MEDICAL_FACILITY" ||
    nature === "LAW_OFFICE" ||
    nature === "INSURANCE_COMPANY"
  ) {
    return nature;
  }

  return "MEDICAL_FACILITY";
};

export const resolveOrganizationIdFromUser = (userData) =>
  Number(
    userData?.organization_id ||
      userData?.organizationId ||
      userData?.account?.organizationId ||
      0,
  );

export const appendOrganizationIdToPayload = (payload = {}, userData) => {
  const organizationId = resolveOrganizationIdFromUser(userData);
  if (organizationId > 0) {
    return {
      ...payload,
      organization_id: organizationId,
    };
  }
  return { ...payload };
};
